'use client'

import { motion } from "framer-motion"
import { AlertTriangle, CircleCheck, Gift, Info, Medal, Ticket, TicketPercent, Truck, X } from 'lucide-react'
import Input from "@/components/elements/Input"
import { useEffect, useState } from "react"
import { toast } from "@/lib/toast"
import { useCart, useAuth } from "@/lib/commerce/hooks"
import { getLoyaltyVouchersApi } from "@/lib/commerce/services/loyalty-api"
import { formatPrice, formatImageURL, formatDateTimeNormal, isCouponActive, isGiftCardActive, isLoyaltyActive } from "@/utils/util"
import { CustomBadge } from "@/components/elements/CustomBadge"
import { Button } from "@/components/elements/Button"
import { navigateUrl, sendClientRequest } from "@/utils/client-request"
import Link from "next/link"
import { usePathname } from "next/navigation"
import Tabs from "@/components/elements/Tabs"

export default function DiscountTabs({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [couponCode, setCouponCode] = useState('')
  const [giftCardCode, setGiftCardCode] = useState('')
  const [isLoadingCoupon, setIsLoadingCoupon] = useState(false)
  const [isLoadingGiftCard, setIsLoadingGiftCard] = useState(false)
  const [couponCondition, setCouponCondition] = useState<any>(null)

  const { appliedCoupon, applyCoupon, removeCoupon, couponDiscount, appliedGiftCard, applyGiftCard, removeGiftCard, appliedLoyaltyVoucher, applyLoyaltyVoucher, removeLoyaltyVoucher, amount, sale, allProducts } = useCart()
  const { session, isLoggedIn } = useAuth()
  const [loyaltyVouchers, setLoyaltyVouchers] = useState<any[]>([])
  const [loyaltyProgram, setLoyaltyProgram] = useState<any>(null)

  const subtotal = amount - sale

  const loyaltyEnabled = isLoyaltyActive(vendor) && isLoggedIn
  useEffect(() => {
    if (!loyaltyEnabled) return
    let cancelled = false
    getLoyaltyVouchersApi({ vendorId: vendor?.id || vendor, session: session || undefined }).then(({ data }) => {
      if (!cancelled && Array.isArray(data)) setLoyaltyVouchers(data.filter((voucher: any) => voucher.status === 'active'))
    }).catch(() => {})
    sendClientRequest({ url: '/loyalty/tiers', auth: false, headers: { Vendor: vendor?.id || vendor } }).then(({ data }) => {
      if (!cancelled && data) setLoyaltyProgram(data)
    }).catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loyaltyEnabled])

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Хөнгөлөлтийн кодоо оруулна уу')
      return
    }
    setIsLoadingCoupon(true)
    setCouponCondition(null)
    try {
      const result = await applyCoupon(couponCode.trim())
      if (result?.success) {
        toast.success('Хөнгөлөлтийн код амжилттай идэвхжлээ')
        setCouponCode('')
        setCouponCondition(null)
      } else {
        if (result?.condition) {
          setCouponCondition(result.condition)
        } else {
          toast.error(result?.error || 'Хөнгөлөлтийн код буруу эсвэл хугацаа дууссан байна')
        }
      }
    } catch (error) {
      toast.error('Алдаа гарлаа')
      console.error(error)
    } finally {
      setIsLoadingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    removeCoupon()
    setCouponCondition(null)
    toast.success('Хөнгөлөлтийн код хасагдлаа')
  }

  const getCouponInfo = () => {
    if (!appliedCoupon) return null
    const discount = appliedCoupon.discount || appliedCoupon

    let discountText = ''
    let isExceeding = false

    if (discount.type === 'productDiscount' || discount.type === 'order') {
      let valueType = discount.valueType
      let valuePercent = discount.valuePercent
      let valueAmount = discount.valueAmount

      if (!valueType && discount.type === 'productDiscount' && discount.code && allProducts?.length) {
        for (const p of allProducts) {
          const pd = p.product || p
          const match = (p.discounts || pd?.discounts)?.find((d: any) =>
            d.type === 'productDiscount' && d.method === 'code' && d.code === discount.code
          )
          if (match) {
            valueType = match.valueType
            valuePercent = match.valuePercent
            valueAmount = match.valueAmount
            break
          }
        }
      }

      if (valueType === 'percent' && valuePercent) {
        discountText = `${valuePercent}% хямдрал`
      } else if (valueType === 'amount' && valueAmount) {
        discountText = `${formatPrice(valueAmount)} хямдрал`
        isExceeding = valueAmount > subtotal
      } else if (valueType === 'free') {
        discountText = 'Үнэгүй'
      }
    } else if (discount.type === 'buyXgetY') {
      let matchMode = discount.buyXMatchMode
      let triggerCount = discount.triggerTargets?.length || 1
      let minQty = discount.buyXMinQuantity || 1
      let maxReward = discount.getYMaxQuantity || 1

      if (!matchMode && discount.id && allProducts?.length) {
        for (const p of allProducts) {
          if (p.isRewardItem) continue
          const pd = p.product || p
          const match = (p.discounts || pd?.discounts)?.find((d: any) => d.type === 'buyXgetY' && d.id === discount.id)
          if (match) {
            matchMode = match.buyXMatchMode
            triggerCount = match.triggerTargets?.length || 1
            minQty = match.buyXMinQuantity || 1
            maxReward = match.getYMaxQuantity || 1
            break
          }
        }
      }

      const buyCount = matchMode === 'all' ? triggerCount * minQty : minQty
      discountText = `${buyCount} авбал ${maxReward} бэлэг`
    } else if (discount.type === 'freeShipping' || appliedCoupon.freeShipping) {
      discountText = 'Үнэгүй хүргэлт'
    }

    if (!discountText && couponDiscount > 0) {
      discountText = `${formatPrice(couponDiscount)} хямдрал`
    }

    return { discountText: discountText || 'Хөнгөлөлт идэвхжсэн', isExceeding, discountName: discount.name || appliedCoupon.name }
  }

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      toast.error('Бэлгийн картын кодыг оруулна уу')
      return
    }
    setIsLoadingGiftCard(true)
    try {
      const success = await applyGiftCard(giftCardCode.trim())
      if (success) {
        toast.success('Бэлгийн карт амжилттай идэвхжлээ')
        setGiftCardCode('')
      } else {
        toast.error('Бэлгийн картын код буруу эсвэл хэрэглэгдсэн байна')
      }
    } catch (error) {
      toast.error('Алдаа гарлаа')
      console.error(error)
    } finally {
      setIsLoadingGiftCard(false)
    }
  }

  const handleRemoveGiftCard = () => {
    removeGiftCard()
    toast.success('Бэлгийн карт хасагдлаа')
  }

  const giftCardAmount = Number(appliedGiftCard?.amount) || 0
  const isGiftCardExceedsCart = giftCardAmount > subtotal
  const couponInfo = getCouponInfo()

  const CouponContent = (
    <div className="space-y-3">
      {appliedCoupon && couponInfo ? (
        <>
          <div className="flex items-center justify-between p-3 bg-blue/5 border border-dashed border-blue/50 rounded-lg">
            <div className="flex items-center gap-3">
              {(appliedCoupon.freeShipping || (appliedCoupon.discount || appliedCoupon)?.type === 'freeShipping')
                ? <Truck className="w-5 h-5 text-blue shrink-0" />
                : <TicketPercent className="w-5 h-5 text-blue shrink-0" />
              }
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-foreground">{couponInfo.discountName}</span>
                  <span className="text-xs text-muted-foreground">{couponInfo.discountText}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CustomBadge size="sm" variant="blue">
                    <span>{(appliedCoupon.discount || appliedCoupon)?.code}</span>
                  </CustomBadge>
                  {couponDiscount > 0 && (
                    <span className="text-xs bg-danger/10 text-danger px-1.5 py-0.5 rounded font-semibold">-{formatPrice(couponDiscount)}</span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" icon={X} onClick={handleRemoveCoupon} className="text-muted-foreground hover:text-danger" />
          </div>

          {couponInfo.isExceeding && (
            <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
              <div className="text-xs text-orange-800">
                <div className="font-medium">Анхаар!</div>
                <div className="mt-1">
                  Хөнгөлөлтийн дүн нь сагсны дүн ({formatPrice(subtotal)})-ээс их байна. Та сагсандаа дахин бараа нэмж, хөнгөлөлтийг бүрэн ашиглана уу.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <Input placeholder="Хөнгөлөлтийн код оруулах" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} leftIcon={TicketPercent} className="w-full" color={vendor?.color?.primary} onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()} hasButton buttonText="" buttonIcon={CircleCheck} buttonAction={handleApplyCoupon} disabled={isLoadingCoupon} />
          {couponCondition && (
            <div className="relative overflow-hidden rounded-xl border border-amber-200 bg-amber-50">
              <div className="p-3.5">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 bg-amber-500/10 rounded-lg shrink-0">
                    {couponCondition.discountType === 'freeShipping' ? <Truck className="w-4.5 h-4.5 text-amber-600" /> : couponCondition.discountType === 'buyXgetY' ? <Gift className="w-4.5 h-4.5 text-amber-600" /> : <TicketPercent className="w-4.5 h-4.5 text-amber-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-800">{couponCondition.discountName}</div>
                    <div className="text-xs text-amber-600 mt-0.5">
                      {couponCondition.type === 'minOrderAmount'
                        ? `${formatPrice(couponCondition.required)} захиалгад ${formatPrice(couponCondition.required - couponCondition.current)} дутуу байна`
                        : `${couponCondition.required} ширхэгээс ${couponCondition.required - couponCondition.current} дутуу байна`
                      }
                    </div>
                    {couponCondition.triggerProducts?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {couponCondition.triggerProducts.map((p: any) => (
                          <Link key={p.id} href={navigateUrl(`/product/${p.customId}`, pathname)} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-amber-200 rounded-lg hover:border-amber-400 transition-colors">
                            {p.image && (
                              <img src={formatImageURL(p.image)} alt={p.name} className="w-8 h-8 rounded object-cover" />
                            )}
                            <span className="text-xs font-medium text-amber-800 max-w-[120px] truncate">{p.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setCouponCondition(null)} className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-amber-100 transition-colors shrink-0">
                    <X className="w-4 h-4 text-amber-500" />
                  </button>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-amber-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-amber-500 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (couponCondition.current / couponCondition.required) * 100)}%` }} />
                  </div>
                  <div className="flex justify-between mt-1.5 text-xs text-amber-600">
                    <span>{couponCondition.type === 'minOrderAmount' ? formatPrice(couponCondition.current) : `${couponCondition.current} ширхэг`}</span>
                    <span>{couponCondition.type === 'minOrderAmount' ? formatPrice(couponCondition.required) : `${couponCondition.required} ширхэг`}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )

  const GiftCardContent = (
    <div className="space-y-3">
      {appliedGiftCard ? (
        <>
          <div className="relative overflow-hidden rounded-xl border border-purple-200 bg-linear-to-r from-purple-50 to-violet-50">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-purple-500 rounded-l-xl" />
            <div className="flex items-center justify-between p-3.5 pl-5">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 bg-purple-500/10 rounded-lg">
                  <Gift className="w-4.5 h-4.5 text-purple-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-purple-700">
                    Карт #{appliedGiftCard.customId}
                  </div>
                  <div className="text-xs text-purple-600 mt-0.5">
                    {formatPrice(giftCardAmount)} хөнгөлөлт
                  </div>
                </div>
              </div>
              <button onClick={handleRemoveGiftCard} className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-purple-100 transition-colors">
                <X className="w-4 h-4 text-purple-500" />
              </button>
            </div>
          </div>

          {isGiftCardExceedsCart && (
            <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
              <div className="text-xs text-orange-800">
                <div className="font-medium">Анхаар!</div>
                <div className="mt-1">
                  Бэлгийн картын дүн ({formatPrice(giftCardAmount)}) нь сагсны дүн ({formatPrice(subtotal)})-ээс их байна. Та сагсандаа дахин бараа нэмж, бэлгийн картыг ашиглана уу.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Input placeholder="Бэлгийн картын код" value={giftCardCode} onChange={(e) => setGiftCardCode(e.target.value)} className="w-full" leftIcon={Gift} color={vendor?.color?.primary} onKeyDown={(e) => e.key === 'Enter' && handleApplyGiftCard()} hasButton buttonText="" buttonIcon={CircleCheck} buttonAction={handleApplyGiftCard} disabled={isLoadingGiftCard} />
      )}
    </div>
  )

  const appliedVoucherAmount = Number(appliedLoyaltyVoucher?.remainingAmount ?? appliedLoyaltyVoucher?.amount) || 0
  const voucherExceedsCart = appliedVoucherAmount > subtotal

  const LoyaltyVoucherContent = (
    <div className="space-y-3">
      {appliedLoyaltyVoucher ? (
        <>
          <div className="relative overflow-hidden rounded-xl border border-violet-300 bg-linear-to-r from-violet-50/60 to-white">
            <div className="flex items-center justify-between gap-3 p-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center justify-center w-9 h-9 bg-violet-500/10 rounded-lg shrink-0">
                  <Ticket className="w-4.5 h-4.5 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold font-mono text-foreground truncate">{appliedLoyaltyVoucher.code}</div>
                  <div className="text-xs text-violet-600 mt-0.5 flex items-center gap-1">
                    <CircleCheck className="w-3.5 h-3.5" />
                    Идэвхжсэн
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 pl-3 border-l border-dashed border-violet-200">
                <div className="text-sm font-bold tabular-nums text-violet-700">-{formatPrice(appliedVoucherAmount)}</div>
                <button onClick={() => { removeLoyaltyVoucher(); toast.success('Ваучер хасагдлаа') }} className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-violet-100 transition-colors">
                  <X className="w-4 h-4 text-violet-500" />
                </button>
              </div>
            </div>
          </div>
          {!loyaltyProgram?.voucherPartialUse && voucherExceedsCart ? (
            <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
              <div className="text-xs text-orange-800">
                <div className="font-medium">Анхаар!</div>
                <div className="mt-1">Ваучер захиалгад бүтнээрээ зарцуулагдана. Захиалгын дүн ваучерын дүнгээс бага тул үлдэх {formatPrice(appliedVoucherAmount - subtotal)} буцаагдахгүй.</div>
              </div>
            </div>
          ) : (
            <p className="flex items-start gap-1.5 px-1 text-xs text-muted-foreground">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {loyaltyProgram?.voucherPartialUse
                  ? 'Ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.'
                  : 'Ваучер захиалгад бүтнээрээ зарцуулагдана.'}
              </span>
            </p>
          )}
        </>
      ) : loyaltyVouchers.length > 0 ? (
        <div className="space-y-2">
          {loyaltyVouchers.map((voucher) => (
            <div key={voucher.id} className="relative overflow-hidden rounded-xl border border-violet-200 bg-linear-to-r from-violet-50/60 to-white">
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center justify-center w-9 h-9 bg-violet-500/10 rounded-lg shrink-0">
                    <Ticket className="w-4.5 h-4.5 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold font-mono text-foreground truncate">{voucher.code}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 pl-3 border-l border-dashed border-violet-200">
                  <div className="text-sm font-bold tabular-nums text-violet-700">{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</div>
                  <Button size="xs" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => { applyLoyaltyVoucher(voucher); toast.success('Ваучер идэвхжлээ') }}>Ашиглах</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3 p-3.5 border border-dashed border-border rounded-xl">
          <div className="flex items-center justify-center w-9 h-9 bg-muted rounded-lg shrink-0">
            <Ticket className="w-4.5 h-4.5 text-muted-foreground" />
          </div>
          <div className="text-sm text-muted-foreground">
            Танд идэвхтэй ваучер алга байна. <Link href={navigateUrl('/loyalty', pathname)} className="underline hover:text-foreground">Оноогоо ваучер болгох</Link>
          </div>
        </div>
      )}
    </div>
  )

  const canUseCoupon = isCouponActive(vendor)
  const canUseGiftCard = isGiftCardActive(vendor)
  const canUseLoyaltyVoucher = loyaltyEnabled && (loyaltyVouchers.length > 0 || !!appliedLoyaltyVoucher)

  const responsiveLabel = (short: string, full: string) => (
    <>
      <span className="sm:hidden">{short}</span>
      <span className="hidden sm:inline">{full}</span>
    </>
  )

  const tabs = [
    ...(canUseCoupon ? [{ label: responsiveLabel('Код', 'Хөнгөлөлтийн код'), value: 'coupon', icon: <TicketPercent className="w-4 h-4" />, content: CouponContent }] : []),
    ...(canUseGiftCard ? [{ label: responsiveLabel('Карт', 'Бэлгийн карт'), value: 'giftcard', icon: <Gift className="w-4 h-4" />, content: GiftCardContent }] : []),
    ...(canUseLoyaltyVoucher ? [{ label: 'Ваучер', value: 'loyalty', icon: <Medal className="w-4 h-4" />, content: LoyaltyVoucherContent }] : []),
  ]

  if (!tabs.length) return null

  if (tabs.length === 1) {
    return (
      <motion.div className="mt-8" initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
        {tabs[0].content}
      </motion.div>
    )
  }

  return (
    <motion.div className="mt-4 sm:mt-8" initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
      <Tabs tabs={tabs} defaultValue={tabs[0].value} contentClassName="mt-2" />
    </motion.div>
  )
}
